import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormContext } from 'components/stateless/Form';
import {
  convertToDealerAgreementResetData,
  convertToInterfaceDealerAgreementData,
} from 'models/convertor/dealerAgreement';
import type { DealerAgreementDetailVOModel } from 'models/vo/DealerAgreementDetailVO';
import { requestFinancierDealerAgreementDetailByIdInterface } from 'utils/http/api/financier/dealer-agreements';
import { showLoadingUI, unShowLoadingUI } from 'utils/loadingUIManager/loadingUIManager';
import type { ModalOptions } from 'utils/modal/ModalWrapper';
import { ModalSize, ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';

import SearchInterfaceByContactNoModal from '../../../modals/SearchInterfaceByContactNoModal';
import SearchInterfaceByIdModal from '../../../modals/SearchInterfaceByIdModal';
import useAgreementViewModel from '../../../models/agreement/useAgreementViewModel';
import useAnchorAgreementInfoViewModel from '../../../models/agreement/useAnchorAgreementInfoViewModel';
import useExtraInformationViewModel from '../../../models/extraInformation/useExtraInformationViewModel';

const useRegistrationDetailLoadAgreementController = () => {
  const { show: showModal, id: modalId } = useModal();
  const { t } = useTranslation(['format']);

  const { dealerAgreementId, supportedCollateralType, useAgreementInterfaceByKeyUnion, useAnchorUserInterface } =
    useExtraInformationViewModel();

  const { updateAllAgreement, updateIsSearchedAgreement } = useAgreementViewModel();

  const { anchorAgreementInfo } = useAnchorAgreementInfoViewModel();

  const interfaceDataRef = useRef<DealerAgreementDetailVOModel>({} as DealerAgreementDetailVOModel);

  const {
    methods: { reset },
  } = useFormContext();

  const setSearchedInterfaceData = useCallback(searchedData => {
    interfaceDataRef.current = searchedData;
  }, []);

  const handleApplyClick = () => {
    const convertedInterfaceAgreement = convertToInterfaceDealerAgreementData({
      prevAnchorAgreementData: anchorAgreementInfo,
      interfaceData: interfaceDataRef.current,
      useAnchorUserInterface,
    });

    if (convertedInterfaceAgreement) {
      updateIsSearchedAgreement(true);
      updateAllAgreement({
        ...convertedInterfaceAgreement,
        dealerAgreementId,
      });
      reset(convertToDealerAgreementResetData(convertedInterfaceAgreement));
    }
  };

  const updateDealerAgreementModalOptions: ModalOptions = {
    modalType: ModalType.CONFIRM,
    modalSize: ModalSize.XL,
    title: t('text:Update_Agreement'),
    confirmBtnText: t('text:Apply'),
    closeBtnText: t('text:Close'),
    confirmBtnCb: handleApplyClick,
  };

  const showSearchInterfaceByIdModal = async () => {
    try {
      showLoadingUI();
      const interfaceData = await requestFinancierDealerAgreementDetailByIdInterface(dealerAgreementId);
      setSearchedInterfaceData(interfaceData);
      showModal(<SearchInterfaceByIdModal searchedInterfaceData={interfaceData} />, updateDealerAgreementModalOptions);
    } catch (e) {
      showModal(e);
    } finally {
      unShowLoadingUI();
    }
  };

  const showSearchInterfaceByContactNoModal = () => {
    showModal(
      <SearchInterfaceByContactNoModal
        modalId={modalId}
        collateralType={supportedCollateralType}
        getInterfaceData={setSearchedInterfaceData}
      />,
      updateDealerAgreementModalOptions,
    );
  };

  const handleLoadAgreementButtonClick = () => {
    useAgreementInterfaceByKeyUnion ? showSearchInterfaceByIdModal() : showSearchInterfaceByContactNoModal();
  };

  return { handleLoadAgreementButtonClick };
};

export default useRegistrationDetailLoadAgreementController;
